// SPDX-License-Identifier: AGPL-3.0-or-later
// Copyright (C) 2026 Ben Richardson — https://benrichardson.dev
// Additional terms under AGPL-3.0 section 7(b) apply; see ADDITIONAL-TERMS.md.
// Formatting helpers. Pure functions — unit-tested in tests/format.test.ts.

/** Compact currency: $1.2M, $340k, $1,234. */
export function fmtMoney(n: number): string {
  const sign = n < 0 ? '-' : '';
  const a = Math.abs(n);
  if (a >= 1_000_000_000) return `${sign}$${trim(a / 1_000_000_000)}B`;
  if (a >= 1_000_000) return `${sign}$${trim(a / 1_000_000)}M`;
  if (a >= 1_000) return `${sign}$${trim(a / 1_000)}k`;
  return `${sign}$${Math.round(a).toLocaleString('en-AU')}`;
}

function trim(x: number): string {
  // 1 decimal, but drop a trailing .0
  const r = Math.round(x * 10) / 10;
  return Number.isInteger(r) ? String(r) : r.toFixed(1);
}

/** Exact currency with cents, grouped: $1,234,567.89. */
export function fmtMoneyFull(n: number): string {
  return n.toLocaleString('en-AU', { style: 'currency', currency: 'AUD' });
}

/** Exact whole-dollar currency, grouped: $1,234,568 (sign before the $). */
export function fmtDollars(n: number): string {
  const r = Math.round(n);
  const sign = r < 0 ? '-' : '';
  return `${sign}$${Math.abs(r).toLocaleString('en-AU')}`;
}

export function fmtNumber(n: number): string {
  return Math.round(n).toLocaleString('en-AU');
}

export function fmtPercent(n: number, digits = 0): string {
  return `${(n * 100).toFixed(digits)}%`;
}

/** "2026Q01" -> "Jan–Mar 2026". */
export function fmtQuarter(key: string): string {
  const m = /(\d{4})Q(\d{2})/.exec(key);
  if (!m) return key;
  const spans: Record<string, string> = { '01': 'Jan–Mar', '02': 'Apr–Jun', '03': 'Jul–Sep', '04': 'Oct–Dec' };
  return `${spans[m[2]] ?? m[2]} ${m[1]}`;
}

/** "2026Q01" -> "26 Q1". */
export function fmtQuarterShort(key: string): string {
  const m = /(\d{4})Q(\d{2})/.exec(key);
  if (!m) return key;
  return `${m[1].slice(2)} Q${Number(m[2])}`;
}

const STATE_NAMES: Record<string, string> = {
  NSW: 'New South Wales',
  VIC: 'Victoria',
  QLD: 'Queensland',
  WA: 'Western Australia',
  SA: 'South Australia',
  TAS: 'Tasmania',
  ACT: 'Australian Capital Territory',
  NT: 'Northern Territory',
};
export function stateName(code: string): string {
  return STATE_NAMES[code] ?? code;
}

export function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      default: return '&#39;';
    }
  });
}
